import type { CrudArgs } from "./composables/ContextVoter";
import type { AggregateVoterCheck, ContextVoterInterface } from "./composables/ContextVoter.interface";
import { PermissionAttributesEnum } from "@verbleif/lib/src/composables/Permissions/permissions.enum";
import { ContextVoter } from "./composables/ContextVoter";

/**
 * Mirrors API ClientRoleVoter: create/update/delete require a client (or *_any) grant.
 * Read may also come from a location (or related) permission under that client.
 */
export class ClientRoleVoter extends ContextVoter implements ContextVoterInterface {
  private holdsOnClient(
    client: string | null | undefined,
    attribute: PermissionAttributesEnum,
  ): boolean {
    if (!client) {
      return false;
    }

    return this.permissionService.getClientPermissionForUser({
      clientIri: client,
      attribute,
    }) !== null
    || this.permissionService.getScopeLessPermissionForUser({
      client,
      attribute,
    }) !== null;
  }

  private canMutate(
    item: CrudArgs | undefined,
    globalAttribute: PermissionAttributesEnum,
    contextAttribute: PermissionAttributesEnum,
  ): boolean {
    if (this.permissionService.getGlobalPermissionForUser({ attribute: globalAttribute })) {
      return true;
    }

    return this.holdsOnClient(
      item?.client ?? this.permissionService.defaultClient,
      contextAttribute,
    );
  }

  public canCreate: ContextVoterInterface["canCreate"] = (item) => {
    return this.canMutate(
      item,
      PermissionAttributesEnum.GlobalOperationClientroleCreateany,
      PermissionAttributesEnum.ContextOperationClientroleCreate,
    );
  };

  public canRead: ContextVoterInterface["canRead"] = (item) => {
    return this._canCrud({
      item,
      globalAttribute: PermissionAttributesEnum.GlobalOperationClientroleReadany,
      contextAttribute: PermissionAttributesEnum.ContextOperationClientroleRead,
    });
  };

  public canUpdate: ContextVoterInterface["canUpdate"] = (item = {
    client: this.permissionService.defaultClient,
    location: this.permissionService.defaultLocation,
  }) => {
    return this.canMutate(
      item,
      PermissionAttributesEnum.GlobalOperationClientroleUpdateany,
      PermissionAttributesEnum.ContextOperationClientroleUpdate,
    );
  };

  public canDelete: ContextVoterInterface["canDelete"] = (item = {
    client: this.permissionService.defaultClient,
    location: this.permissionService.defaultLocation,
  }) => {
    return this.canMutate(
      item,
      PermissionAttributesEnum.GlobalOperationClientroleDeleteany,
      PermissionAttributesEnum.ContextOperationClientroleDelete,
    );
  };

  public canManage: AggregateVoterCheck = (item = {
    client: this.permissionService.defaultClient,
    location: this.permissionService.defaultLocation,
  }) => {
    return this.canCreate(item) || this.canUpdate(item) || this.canDelete(item);
  };

  public canAccess(item?: CrudArgs): boolean {
    return this.canRead(item ?? {
      client: this.permissionService.defaultClient,
      location: this.permissionService.defaultLocation,
    });
  }
}
